import React, { useCallback, useMemo, useState } from 'react';
import {
  DEFAULT_SUPPORT_GROUP_ID,
  SUPPORT_GROUPS,
  getSupportGroupById,
  getSupportSubcategoryById,
  isDirectFormSupportGroup,
  validateSupportMessage,
  SUPPORT_MESSAGE_MAX,
  SUPPORT_MESSAGE_MIN,
} from '../content/helpSupportCategories.js';
import { submitSupportTicket } from '../utils/supportTicketApi.js';

function SupportSubcategoryRow({ item, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      className="w-full text-left px-1 py-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50/80 min-h-[44px]"
    >
      <span className="block text-sm md:text-base font-medium text-gray-900">{item.title}</span>
      <span className="block text-xs md:text-sm text-gray-500 mt-1 leading-snug">{item.subtitle}</span>
    </button>
  );
}

function SupportTicketForm({
  categoryId,
  groupTitle,
  subcategoryTitle,
  onBack,
  onSuccess,
}) {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const subcategory = getSupportSubcategoryById(categoryId);
  const isUrgent = subcategory?.route === 'urgent';

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateSupportMessage(message);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const result = await submitSupportTicket({ category: categoryId, message });
      onSuccess({
        ticketId: result.ticketId,
        categoryId,
        route: subcategory?.route ?? 'general',
      });
    } catch (err) {
      setError(err?.message || 'Failed to submit support request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 min-h-[44px]"
      >
        <span aria-hidden>←</span>
        Change issue type
      </button>

      <nav aria-label="Breadcrumb" className="text-xs text-gray-500">
        <span>Help &amp; Support</span>
        <span className="mx-1.5" aria-hidden>
          ›
        </span>
        <span>Contact</span>
        <span className="mx-1.5" aria-hidden>
          ›
        </span>
        <span className="text-gray-700">{groupTitle}</span>
        {subcategoryTitle && (
          <>
            <span className="mx-1.5" aria-hidden>
              ›
            </span>
            <span className="text-gray-700">{subcategoryTitle}</span>
          </>
        )}
      </nav>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900">
          {subcategoryTitle || 'Other'}
        </h3>
        {categoryId === 'other' && (
          <p className="mt-2 text-sm text-gray-600">
            Describe your issue and we&apos;ll email you a ticket confirmation.
          </p>
        )}
        {isUrgent && (
          <p className="mt-2 text-sm text-gray-600">
            We&apos;ll open a private Discord thread and notify the team.
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="support-message" className="block text-sm font-medium text-gray-700 mb-1.5">
            Description
          </label>
          <textarea
            id="support-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={8}
            maxLength={SUPPORT_MESSAGE_MAX}
            placeholder="Tell us what happened and what you expected instead."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={submitting}
          />
          <p className="mt-1 text-xs text-gray-500">
            {message.trim().length} / {SUPPORT_MESSAGE_MAX} (min {SUPPORT_MESSAGE_MIN})
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center min-h-[44px] px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </form>
    </div>
  );
}

function SupportSuccessPanel({ ticketId, route, onReset }) {
  const isUrgent = route === 'urgent';

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-gray-900">Request received</h3>
      <p className="text-sm text-gray-700 leading-relaxed">
        Ticket <span className="font-mono text-gray-900">{ticketId}</span>
        {isUrgent
          ? ' — we are setting up your Discord thread. Refresh this page or check Discord shortly.'
          : ' — we emailed you a confirmation. Our team will follow up by email.'}
      </p>
      <button
        type="button"
        onClick={onReset}
        className="text-sm font-medium text-blue-600 hover:text-blue-800 min-h-[44px]"
      >
        Submit another request
      </button>
    </div>
  );
}

export default function ContactSupportPanel() {
  const [selectedGroupId, setSelectedGroupId] = useState(DEFAULT_SUPPORT_GROUP_ID);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [successState, setSuccessState] = useState(null);

  const activeGroup = useMemo(
    () => getSupportGroupById(selectedGroupId) ?? SUPPORT_GROUPS[0],
    [selectedGroupId]
  );

  const showingForm = Boolean(selectedCategoryId);
  const showingSuccess = Boolean(successState);

  const selectGroup = useCallback((groupId) => {
    const group = getSupportGroupById(groupId);
    if (!group) {
      return;
    }
    setSelectedGroupId(groupId);
    setSuccessState(null);
    if (isDirectFormSupportGroup(group)) {
      setSelectedCategoryId('other');
    } else {
      setSelectedCategoryId(null);
    }
  }, []);

  const selectSubcategory = useCallback((categoryId) => {
    setSelectedCategoryId(categoryId);
    setSuccessState(null);
  }, []);

  const backToSubcategories = useCallback(() => {
    if (isDirectFormSupportGroup(activeGroup)) {
      setSelectedCategoryId(null);
      setSelectedGroupId(DEFAULT_SUPPORT_GROUP_ID);
      return;
    }
    setSelectedCategoryId(null);
    setSuccessState(null);
  }, [activeGroup]);

  const resetFlow = useCallback(() => {
    setSuccessState(null);
    setSelectedCategoryId(null);
    setSelectedGroupId(DEFAULT_SUPPORT_GROUP_ID);
  }, []);

  if (!activeGroup) {
    return null;
  }

  return (
    <div className="flex flex-col md:flex-row h-full min-h-0">
      <nav
        aria-label="Contact support categories"
        className="md:w-56 lg:w-64 flex-shrink-0 border-b md:border-b-0 md:border-r border-gray-200 bg-gray-50/60 md:bg-white py-2 md:overflow-y-auto"
      >
        <p className="hidden md:block px-4 pt-2 pb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Topics
        </p>
        <ul className="flex md:flex-col gap-0 overflow-x-auto md:overflow-visible">
          {SUPPORT_GROUPS.map((group) => {
            const isActive = group.id === activeGroup.id;
            return (
              <li key={group.id} className="flex-shrink-0 md:flex-shrink">
                <button
                  type="button"
                  onClick={() => selectGroup(group.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`w-full text-left px-4 py-2.5 md:py-3 text-sm whitespace-nowrap md:whitespace-normal transition-colors border-l-0 md:border-l-2 ${
                    isActive
                      ? 'md:border-blue-600 bg-white md:bg-blue-50/40 text-gray-900 font-medium'
                      : 'md:border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                  }`}
                >
                  {group.title}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="flex-1 p-5 md:p-8 min-w-0 overflow-y-auto">
        {showingSuccess ? (
          <SupportSuccessPanel
            ticketId={successState.ticketId}
            route={successState.route}
            onReset={resetFlow}
          />
        ) : showingForm ? (
          <SupportTicketForm
            categoryId={selectedCategoryId}
            groupTitle={activeGroup.title}
            subcategoryTitle={
              selectedCategoryId === 'other'
                ? null
                : getSupportSubcategoryById(selectedCategoryId)?.title
            }
            onBack={backToSubcategories}
            onSuccess={setSuccessState}
          />
        ) : (
          <>
            <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-4">
              <span>Help &amp; Support</span>
              <span className="mx-1.5" aria-hidden>
                ›
              </span>
              <span className="text-gray-700">Contact</span>
              <span className="mx-1.5" aria-hidden>
                ›
              </span>
              <span className="text-gray-700">{activeGroup.title}</span>
            </nav>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{activeGroup.title}</h3>
            {isDirectFormSupportGroup(activeGroup) ? (
              <p className="text-sm text-gray-600 mb-6">{activeGroup.formHint}</p>
            ) : (
              <p className="text-sm text-gray-600 mb-6">Select the issue that best matches your situation.</p>
            )}

            <div className="divide-y divide-gray-200 border-t border-gray-200">
              {(activeGroup.items ?? []).map((item) => (
                <SupportSubcategoryRow key={item.id} item={item} onSelect={selectSubcategory} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
